<template>
    <modal :show.sync="show" modalClasses="medium-sized-modal" :hide-footer="true">
        <h3 slot="header" class="mb-0 font-weight-500">Assign To</h3>
        
        <b-form>
            <b-row>
                <b-col cols="12" v-if="show_hub_manager">
                    <b-form-group label="Hub Manager:">
                        <el-select 
                            v-model="selected_hub_manager_id" 
                            placeholder="Select" 
                            filterable 
                            :disabled="!checkAppCodeAndRoleType(['sas'], ['admin', 'manager', 'director'])">
                            <el-option value=""></el-option>
                            <el-option
                                v-for="item in userHubManagerList || []"
                                :key="item.id"
                                :label="item.name"
                                :value="item.id">
                            </el-option>
                        </el-select>
                    </b-form-group>
                </b-col>

                <b-col cols="12" v-if="show_ssa">
                    <b-form-group label="Senior Sales Advisor:">
                        <el-select 
                            v-model="selected_ssa_id" 
                            placeholder="Select" 
                            filterable 
                            :disabled="!(checkAppCodeAndRoleType(['sas'], ['admin', 'manager', 'director']) || checkAccess(['role_hub_manager']))">
                            <el-option value=""></el-option>
                            <el-option
                                v-for="item in ssaOptions"
                                :key="item.id"
                                :label="item.name"
                                :value="item.id">
                            </el-option>
                        </el-select>
                    </b-form-group>
                </b-col>

                <b-col cols="12" v-if="show_sse">
                    <b-form-group label="Sales Support Executive:">
                        <el-select 
                            v-model="selected_sse_id"  
                            placeholder="Select" 
                            filterable 
                            :disabled="!(checkAppCodeAndRoleType(['sas'], ['admin', 'manager', 'director']) || checkAccess(['role_ssa','role_hub_manager']))">
                            <el-option value=""></el-option>
                            <el-option
                                v-for="item in sseOptions"
                                :key="item.id"
                                :label="item.name"
                                :value="item.id">
                            </el-option>
                        </el-select>
                    </b-form-group>
                </b-col>

                <b-col md="6" sm="12" class="mx-auto text-center mt-4 pt-2">
                    <b-button type="submit" class="w-100" variant="primary" @click.prevent="assignAgent">Confirm</b-button>
                </b-col>
            </b-row>
        </b-form>
    </modal>
</template>

<script>
import Modal from "@/components/Modal"
import { Select, Option } from 'element-ui'
import {
    notifyFunctionMixin,
    helperFunctionMixin,
    axios,
    services,
    getHeader
} from '@/assets/js/httpImports.js'
import mainEventBus from '@/assets/js/mainEventBus'
import { mapState, mapActions } from 'pinia'
import { useAuthStore } from '@/stores/authStore'
import { useAccessControlStore } from '@/stores/accessControlStore'
import { useMessagingStore } from '@/stores/messagingStore'
import { CAN_ASSIGN_ROLE_IDS } from '@/assets/js/roles.js' 

export default {
    props: {
        show_assign_customers_to: Boolean,
        agents_list: Array,
        customer_details: Object,
        multiple_selection: Array,
        show_ssa: Boolean,
        show_sse: Boolean,
        show_hub_manager: Boolean,
    },
    mixins: [
        notifyFunctionMixin,
        helperFunctionMixin
    ],
    components: {
        Modal,
        [Select.name]: Select,
        [Option.name]: Option,
    },
    data() {
        return {
            selected_ids: [],
            selected_ssa_id: '',
            selected_sse_id: '',
            selected_hub_manager_id: '',
            ssaOptions: [],
            sseOptions: [],
            CAN_ASSIGN_ROLE_IDS: CAN_ASSIGN_ROLE_IDS
        }
    },
    computed: {
        show: {
            get() {
                return this.show_assign_customers_to;
            },
            set(newVal) {
                this.$emit('update:show_assign_customers_to', newVal);
            },
        },
        ...mapState(useAuthStore, ['auth_user']),
        ...mapState(useAccessControlStore, ['userSsaList', 'userSseList', 'userHubManagerList']),

        isRestrictedManager() {
            const roleId = Number(this.auth_user?.account_info?.role_id || 0);
            return [6, 7].includes(roleId);
        }
    },
    watch: {
        show(newVal) {
            if (newVal) {
                this.loadTeamMembers();
            }
        },
        customer_details(newVal) {
            if (newVal) {
                this.selected_hub_manager_id = '';
                this.selected_ssa_id = '';
                this.selected_sse_id = '';
            }
        },
        multiple_selection(newVal) {
            if (newVal) {
                this.selected_ids = newVal.map(customer => customer.customer_id);
            }
        }
    },
    created() {
        if (this.show) {
            this.loadTeamMembers();
        }
    },
    methods: {
        ...mapActions(useMessagingStore, ['getProfileBundleKey', 'checkUserGroup']),

        async loadTeamMembers() {
            if (this.isRestrictedManager) {
                // Restricted managers (6 & 7) → get team members from backend
                try {
                    const app_code = _gAuth.appCode();
                    const corporate_id = _gAuth.activeCorporateId();
                    const user_id = this.auth_user.id;
                    const role_id = this.auth_user.account_info.role_id;

                    const members = await this.getHubTeamMembers(app_code, corporate_id, user_id, role_id);

                    this.ssaOptions = (members || []).filter(user => 
                        String(user.id) !== String(this.auth_user.id)
                    );
                    this.sseOptions = [...this.ssaOptions]; // same list for both
                } catch (error) {
                    this.ssaOptions = [];
                    this.sseOptions = [];
                }
            } else {
                // All other roles (admin, director, etc.) → use full original lists
                this.ssaOptions = this.userSsaList || [];
                this.sseOptions = this.userSseList || [];
            }
        },

        async getHubTeamMembers(app_code, corporate_id, user_id, role_id) {
            try {
                const params = new URLSearchParams({
                    application_code: app_code,
                    corporate_id: corporate_id,
                    user_id: user_id,
                    role_id: role_id
                });

                const res = await axios.get(
                    services.commission + 'team-member/hub-team-members?' + params.toString(),
                    { headers: getHeader() }
                );

                return res.data.data || [];
            } catch (e) {
                return [];
            }
        },

        assignAgent() {
            if (this.multiple_selection.length > 0) {
                let form = {
                    ids: this.selected_ids
                };

                if (this.selected_ssa_id !== '' && this.selected_ssa_id !== null) {
                    form.ssa_id = this.selected_ssa_id;
                }

                if (this.selected_sse_id !== '' && this.selected_sse_id !== null) {
                    form.sse_id = this.selected_sse_id;
                }

                form.hub_manager_id = this.selected_hub_manager_id;

                axios.post(services.customer + 'customers/assign_agent_bulk', form, { headers: getHeader() })
                    .then(res => {
                        this.autoSetChatGroup();
                        this.notifySuccess('<b>Customers Agents - Successfully Updated.');
                        mainEventBus.$emit('refresh-table-data');
                    })
                    .catch(e => {
                        this.errorFunction(e);
                    });
            } else {
                let params = {};

                if (this.selected_ssa_id !== '' && this.selected_ssa_id !== null) {
                    params['ssa_id'] = this.selected_ssa_id;
                }

                if (this.selected_sse_id !== '' && this.selected_sse_id !== null) {
                    params['sse_id'] = this.selected_sse_id;
                }

                if (this.selected_hub_manager_id !== '' && this.selected_hub_manager_id !== null) {
                    params['hub_manager_id'] = this.selected_hub_manager_id;
                }

                axios.put(services.customer + 'customers/' + this.customer_details.customer_id + '/assign_agent', params, { headers: getHeader() })
                    .then(res => {
                        this.autoSetChatGroup();
                        this.notifySuccess('<b>Customer Agents: ' + res.data.data.name + '</b> - Successfully Updated.');
                        mainEventBus.$emit('refresh-table-data');
                    })
                    .catch(e => {
                        this.errorFunction(e);
                    });
            }
        },

        autoSetChatGroup() {
            if (_gAuth.isAppSas()) {
                let user_id = null;
                let type = 'customer';
                let channelIds = _gAuth.activeCorpSettings('lead_reassign_only_channel_ids', []);
                const customers = (this.multiple_selection.length > 0) ? this.multiple_selection : [this.customer_details];

                if (!_gHlp.isEmpty(customers)) {
                    if (this.selected_ssa_id !== '' && this.selected_ssa_id !== null) {
                        user_id = this.selected_ssa_id;
                        customers.forEach(item => {
                            let bundle_key = this.getProfileBundleKey('customer', item);
                            let is_add_all_group = true;
                            this.checkUserGroup(user_id, bundle_key, type, is_add_all_group);
                        });
                    }

                    if (this.selected_sse_id !== '' && this.selected_sse_id !== null) {
                        user_id = this.selected_sse_id;
                        customers.forEach(item => {
                            let bundle_key = this.getProfileBundleKey('customer', item);
                            let is_add_all_group = true;
                            this.checkUserGroup(user_id, bundle_key, type, is_add_all_group);
                        });
                    }
                }
            }
        }
    }
}
</script>